/**
 * Blockchain-feed response → canonical rows + graph elements.
 *
 * The feed lambda returns per-chain blocks of `{ transactions, unique_addresses,
 * graph }` (GET) or the same nested under `addresses[]` (POST). This adapter is
 * a thin re-shape over the SHARED normalizers so the Overview widget never forks
 * field names or graph mapping:
 *   - rows  → `coerceTransactionRow` (`@authe/fe-commons` via `@authe/tx-table`):
 *             one place that reconciles `hash`/`transaction_hash` → `tx_hash`,
 *             `from_address` → `from`, the `{ transaction, … }` envelope, etc.
 *   - graph → `feedGraph`: the per-chain graphs merged into ONE multichain
 *             `InteractionGraph` (stamping each node's chain so colours/badges
 *             stay correct), which `toGraph` (`@authe/tx-graph`) then maps onto
 *             React Flow elements.
 *
 * Rendering that graph is `useFeedGraphElements`' job, not this module's: a node
 * has to be drawn through the reader's resolved CATALOG (its label, its tags,
 * its risk) and resolving one is a hook. There used to be a `feedToGraph`
 * convenience here that skipped straight from the wire to elements, and both
 * feed surfaces called it — which is exactly how they ended up as the only
 * graphs in the product with no catalog identity on their nodes.
 *
 * The other feed-specific logic here is building the from/to/entity enrichment
 * overlay the table's forensics columns read.
 */
import { coerceTransactionRow, type TxRow, type TxRowEnrichment } from "@authe/tx-table";
import type { InteractionGraphDTO } from "@authe/fe-commons/models";

interface FeedGraph {
  nodes?: Record<string, unknown>[];
  edges?: Record<string, unknown>[];
  node_count?: number;
  edge_count?: number;
  chain_ids?: string[];
  multi_chain?: boolean;
}

interface FeedChain {
  chain_id?: string;
  transactions?: unknown[];
  unique_addresses?: string[];
  graph?: FeedGraph;
  error?: string;
}

export interface OnchainFeedResponse {
  /** GET (block feed) shape. */
  chains?: FeedChain[];
  /** POST (address feed) shape. */
  addresses?: { address: string; chains?: FeedChain[] }[];
  /**
   * Flat list shape (no `graph: true`): each entry is the `{ transaction,
   * unique_addresses }` envelope `coerceTransactionRow` understands, with a
   * sibling `chain_id`. Returned by the address feed when only rows are needed.
   */
  items?: { chain_id?: string; transaction?: unknown; unique_addresses?: string[] }[];
  /** Optional backend-merged multichain graph (preferred when present). */
  graph?: FeedGraph;
  /**
   * Opaque cursor for the next page, echoed back as the request's `cursor`.
   *
   * Top-level here, unlike the DynamoDB-backed list routes which nest it under
   * `pagination.next`. The shape differs because the position does: this cursor
   * holds one offset PER (provider, chain) lane plus the hashes already served,
   * so a lane that is mid-page and one that is exhausted both resume correctly.
   * Only the POST (address) feed pages; the GET block snapshot answers with each
   * chain's latest and has no next.
   */
  next_cursor?: string | null;
  /** Convenience flag for {@link next_cursor} being present and useful. */
  has_more?: boolean;
  /**
   * One status per `(provider, chain)` lane the address feed read. A lane whose
   * fetch raised is `errored` and served nothing, while the rest of the page
   * still arrives — so a table built from `items` alone reads a failed chain
   * as a quiet one.
   */
  sources?: FeedSourceStatus[];
  metadata?: Record<string, unknown>;
}

export interface FeedSourceStatus {
  provider?: string;
  chain_id?: string;
  status?: string;
  items_served?: number;
  has_more?: boolean;
  last_error?: { error_type?: string; message?: string; retryable?: boolean } | null;
}

/** A `(provider, chain)` lane whose latest fetch failed. */
export interface FeedSourceFailure {
  provider: string;
  chainId: string;
  message?: string;
}

/**
 * Lanes whose most recent fetch failed, across every page loaded.
 *
 * Each page reports every lane's state as of that page, so a later page's word
 * wins: a lane that errored on page one and served page two has recovered, and
 * one that served page one and errored on page two is missing everything after.
 */
export function feedFailedSources(pages: readonly (OnchainFeedResponse | undefined)[]): FeedSourceFailure[] {
  const latest = new Map<string, FeedSourceStatus>();
  for (const page of pages) {
    for (const source of page?.sources ?? []) {
      const provider = str(source.provider) ?? "";
      const chainId = str(source.chain_id) ?? "";
      if (!provider && !chainId) continue;
      latest.set(`${provider}:${chainId}`, source);
    }
  }
  const failures: FeedSourceFailure[] = [];
  for (const source of latest.values()) {
    if (source.status !== "errored") continue;
    const message = str(source.last_error?.message);
    failures.push({
      provider: str(source.provider) ?? "unknown",
      chainId: str(source.chain_id) ?? "",
      ...(message ? { message } : {}),
    });
  }
  return failures;
}

/**
 * The cursor to ask for the next page with, or `null` when the feed is done.
 *
 * Reads `has_more` as the authority and the cursor as the payload: a cursor with
 * `has_more: false` is the terminal position rather than an invitation, and
 * paging on it would re-request lanes the server already reported exhausted.
 */
export function feedNextCursor(feed: OnchainFeedResponse | undefined): string | null {
  if (!feed?.has_more) return null;
  return feed.next_cursor ?? null;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

/** Flatten GET `chains` / POST `addresses[].chains` into one chain list. */
function feedChains(feed: OnchainFeedResponse): FeedChain[] {
  if (Array.isArray(feed.addresses)) return feed.addresses.flatMap((a) => a.chains ?? []);
  return feed.chains ?? [];
}

/** Normalize the one-or-many argument the page-aware entry points accept. */
function feedList(
  feed: OnchainFeedResponse | OnchainFeedResponse[] | undefined,
): OnchainFeedResponse[] {
  if (!feed) return [];
  return Array.isArray(feed) ? feed : [feed];
}

/**
 * Build the forensics enrichment overlay the table reads (`getRowEnrichment`).
 * Derived purely from fields the coerced row already carries — no recompute.
 */
function withEnrichment(row: TxRow): TxRow {
  const r = row as Record<string, unknown>;
  const from = str(r.from) ?? str(r.from_address);
  const to = str(r.to) ?? str(r.to_address);
  const entityName = str(r.to_entity_name) ?? str(r.to_address_label);
  const input = r.input;
  const enrichment: TxRowEnrichment = {
    from,
    to,
    entity: entityName ? { id: to ?? entityName, name: entityName } : null,
    contractInteraction: typeof input === "string" && input.length > 2 && input !== "0x",
  };
  return { ...row, enrichment } as TxRow;
}

/** Every row on ONE page, in the order the feed served them. */
function pageRows(feed: OnchainFeedResponse): TxRow[] {
  const rows: TxRow[] = [];

  // Flat list shape (`result.items[]`) takes precedence: each entry is the
  // `{ transaction, unique_addresses }` envelope coerceTransactionRow unwraps,
  // with a sibling `chain_id` to stamp when the inner tx omits one.
  if (Array.isArray(feed.items)) {
    for (const item of feed.items) {
      const row = coerceTransactionRow(item as Record<string, unknown>);
      if (!row.chain_id && item.chain_id) (row as { chain_id: string }).chain_id = item.chain_id;
      rows.push(withEnrichment(row));
    }
    return rows;
  }

  for (const chain of feedChains(feed)) {
    for (const entry of chain.transactions ?? []) {
      const row = coerceTransactionRow(entry as Record<string, unknown>);
      // Fall back to the chain's id when the tx itself didn't carry one.
      if (!row.chain_id && chain.chain_id) (row as { chain_id: string }).chain_id = chain.chain_id;
      rows.push(withEnrichment(row));
    }
  }
  return rows;
}

/**
 * Normalize every feed transaction into a canonical, enriched `TxRow`.
 *
 * Accepts a LIST of pages as well as one response, so "Load more" appends rather
 * than replaces. Pages arrive newest-first and already hash-deduped by the
 * server WITHIN a page; the cursor also carries the hashes it has served, so
 * consecutive pages do not overlap and concatenation is the right join.
 */
export function feedRows(
  feed: OnchainFeedResponse | OnchainFeedResponse[] | undefined,
): TxRow[] {
  return feedList(feed).flatMap(pageRows);
}

/** Stamp a chain id onto nodes that didn't declare one (keeps merges chain-aware). */
function stampChain(graph: FeedGraph, chainId: string | undefined): {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
} {
  const nodes = (graph.nodes ?? []).map((n) =>
    (n as { chain_id?: unknown }).chain_id == null && chainId ? { ...n, chain_id: chainId } : n,
  );
  return { nodes, edges: graph.edges ?? [] };
}

/**
 * Resolve the feed's interaction graph: prefer a backend-merged top-level graph;
 * otherwise merge the per-chain graphs into one multichain `InteractionGraph`
 * (deduping nodes by id, stamping each node's chain).
 *
 * Accepts a LIST of pages as well as one response, because the graph has to grow
 * with "Load more" rather than snap back to page one's. Node de-duplication is
 * what makes that safe: the same counterparty appearing on three pages is one
 * node, and paging a graph that appended blindly would fan a hub address out
 * into duplicates of itself.
 */
export function feedGraph(
  feed: OnchainFeedResponse | OnchainFeedResponse[] | undefined,
): InteractionGraphDTO {
  const pages = feedList(feed);
  if (pages.length === 0) return { nodes: [], edges: [] } as unknown as InteractionGraphDTO;

  const nodes: Record<string, unknown>[] = [];
  const edges: Record<string, unknown>[] = [];
  const seenNodes = new Set<string>();
  const chainIds = new Set<string>();

  const addNode = (node: Record<string, unknown>): void => {
    const id = String(
      (node as { id?: unknown; address?: unknown }).id ??
        (node as { address?: unknown }).address ??
        "",
    );
    if (id) {
      if (seenNodes.has(id)) return;
      seenNodes.add(id);
    }
    nodes.push(node);
  };

  for (const page of pages) {
    // A backend-merged graph is authoritative for its own page; still merged
    // across pages rather than returned whole, or page two would replace page
    // one's graph instead of extending it.
    const merged = page.graph;
    if (merged && ((merged.nodes?.length ?? 0) > 0 || (merged.edges?.length ?? 0) > 0)) {
      for (const id of merged.chain_ids ?? []) chainIds.add(id);
      for (const node of merged.nodes ?? []) addNode(node);
      for (const edge of merged.edges ?? []) edges.push(edge);
      continue;
    }

    for (const chain of feedChains(page)) {
      if (chain.chain_id) chainIds.add(chain.chain_id);
      if (!chain.graph) continue;
      const stamped = stampChain(chain.graph, chain.chain_id);
      for (const node of stamped.nodes) addNode(node);
      for (const edge of stamped.edges) edges.push(edge);
    }
  }

  return {
    nodes,
    edges,
    node_count: nodes.length,
    edge_count: edges.length,
    chain_ids: [...chainIds],
    multi_chain: chainIds.size > 1,
  } as unknown as InteractionGraphDTO;
}
